import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Form, notification, Select } from 'antd';
import { observe, toJS } from 'mobx';
import moment from 'moment-timezone';
import _ from 'lodash';
import styled from 'styled-components';

import { AppStore, ShopStore, UserStore, CurrentUserStore } from 'stores';

import { StyledPitstopModal } from './common/styled';
import SelectPriorities from './common/SelectPriorities';
import FormMultiSelect from './common/FormMultiSelect';

const StyledP = styled.div`
  font-size: 14px;
  margin-bottom: 20px;
`;

const AddVehicleIssueAlertModal = observer(({ id }) => {
  const [form] = Form.useForm();
  const [state, setState] = useState({
    shopUsers: [],
    users: [],
  });

  const generatePriorityAlerts = (priorities) => {
    return ShopStore.generatePriorityNumbersFromPriorityList(priorities);
  };

  const getShopUsers = async () => {
    const shopUsers = await UserStore.fetchShopUsers(ShopStore.currentShop.id);

    const users = shopUsers
      .filter((user) => user.firstName || user.lastName)
      .map((user) => ({
        name: `${user.firstName} ${user.lastName}`,
        id: user.userId,
      }));

    setState((prev) => ({ ...prev, users: users || [], shopUsers }));
  };

  const init = async () => {
    await getShopUsers();
  };

  useEffect(() => {
    init();

    // MobX observer setup
    const shopChangeWatcher = observe(ShopStore.currentShop, 'id', () => {
      if (ShopStore.isAdmin()) return;
      return init();
    });

    // Cleanup
    return () => {
      shopChangeWatcher();
    };
  }, []);

  const onOkHandler = async () => {
    try {
      const values = await form.validateFields();

      let timezone = CurrentUserStore.user.settings.timezone;
      if (!timezone) {
        timezone = 'America/Toronto';
      }

      const prioritiesAlerts = generatePriorityAlerts(values.priorities).sort();

      const shopNotifications = toJS(
        ShopStore.shopNotifications.get(ShopStore.currentShop.id)
      );

      const priorityAlertsNotifications = _.chain(shopNotifications)
        .get('data.emailConfiguration')
        .filter((a) => a.email_report_type === 'PriorityAlerts')
        .value();

      for (const id of values.users) {
        const userNotifications = priorityAlertsNotifications.filter(
          (a) => a.id_user === id
        );

        let user = state.shopUsers.find((u) => u.userId === id);
        user = {
          id: user.userId,
          ...user,
        };

        if (_.isEmpty(userNotifications)) {
          await ShopStore.checkAndRemoveEmailBasedNotificationsForUser(
            priorityAlertsNotifications,
            user
          );

          await ShopStore.sendEmailPriorityAlertsConfig(
            ShopStore.currentShop.id,
            [user],
            moment(),
            timezone,
            prioritiesAlerts
          );
        } else {
          await ShopStore.updateEmailPriorityAlertsConfig(
            userNotifications[0].id,
            ShopStore.currentShop.id,
            [user],
            prioritiesAlerts
          );
        }
      }

      notification.success({
        message: 'Vehicle Issue Alerts Added',
        description: 'Continue adding alerts or close the modal',
      });

      AppStore.openModals.set(id, false);
      await ShopStore.getShopNotifications(ShopStore.currentShop.id, true);
    } catch (error) {
      if (error.errorFields) {
        notification.error({
          message: 'Error',
          description: 'Please fill in all required fields',
        });
      } else {
        AppStore.addError(
          `Error in updating priority alerts: ${error.message}`
        );
      }
    }
  };

  return (
    <StyledPitstopModal
      id={id}
      title="Add Vehicle Issue Alert"
      width={320}
      okText="Add Alerts"
      closable
      onOk={onOkHandler}
      isManualClose
      cancelButtonProps={{
        type: 'danger',
        ghost: true,
        style: {
          backgroundColor: 'transparent',
          border: 'none',
          color: '#ff4d4f',
          boxShadow: 'none',
        },
      }}
    >
      <StyledP>
        An alert email will be sent whenever an issue with the selected priority
        occurs.
      </StyledP>

      <Form form={form} layout="vertical">
        <Form.Item
          name="users"
          label="Select User(s)"
          rules={[{ required: true, message: 'Please select User(s)' }]}
        >
          <Select
            mode="multiple"
            allowClear
            style={{ width: '100%' }}
            options={state.users.map(({ id, name }) => ({
              value: id,
              label: name,
            }))}
          />
        </Form.Item>

        <FormMultiSelect
          component={<SelectPriorities />}
          name="priorities"
          label="Priority"
          rules={[{ required: true, message: 'Please select a priority' }]}
        />
      </Form>
    </StyledPitstopModal>
  );
});

export default AddVehicleIssueAlertModal;
